import { TwoFactorT } from 'types/twoFactor'
// import * as Sentry from '@sentry/react'
import get from 'lodash/get'
import PACKAGE from '../../package.json'
import jsLogger from 'js-logger'
import config from 'src/config'
import { versions } from 'components/versions'
import { SessionLatencies } from 'types/internal'
import { AvatarLatencyRequest } from 'types/proto'
import { Proto } from 'types/demo3'
import fetchWithRetries from 'shared/utils/fetchWithRetries'

const headers = {
  'Content-Type': 'application/json',
  Accept: 'application/json'
}

export const getNavigatorLanguage = () => {
  if (navigator.languages && navigator.languages.length) {
    return navigator.languages[0]
  } else {
    return (
      get(navigator, 'userLanguage') ||
      get(navigator, 'language') ||
      get(navigator, 'browserLanguage') ||
      'en'
    )
  }
}

export const conversationStart = async (
  urlLang: string | null,
  providedUserId?: string
): Promise<Proto.ConversationStartResponse | Proto.ErrorResponse> => {
  try {
    // Server-authoritative scheme: demo3 bootstrap is served by the always-warm
    // socket VM (POST {socketBase}/demo3/start), not the FF endpoint. FF keeps its
    // own /demo3/start for the currently-deployed client.
    const socketHost = config.socketServerUrl.replace(/^https?:\/\//, '')
    const socketBase =
      socketHost.startsWith('localhost') || socketHost.startsWith('127.')
        ? `http://${socketHost}`
        : `https://${socketHost}`
    const url = `${socketBase}/demo3/start`
    const browserLang = getNavigatorLanguage()
    const params: Proto.ConversationStartRequest = {
      providedUserId,
      slackNotificationsDisabled:
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1',
      version: PACKAGE.version,
      browserLang,
      urlLang: urlLang || undefined,
      libVersions: versions
    }
    jsLogger.log('conversationStart', params)
    const req = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params)
    })
    let body
    try {
      body = await req.json()
    } catch (e) {
      const errMsg =
        e && typeof e === 'object' && 'message' in e
          ? (e as Error).message
          : String(e)
      jsLogger.error('conversationStart: failed to parse json', e)
      // Return a default Proto.ErrorResponse object
      return {
        error: true,
        message: errMsg || 'Failed to parse server response'
      }
    }
    jsLogger.log('conversationStart res', body)
    if (req.status === 200) {
      // Success: body is Proto.ConversationStartResponse
      return body as Proto.ConversationStartResponse
    }
    // Error: body can be Proto.ErrorResponse
    return body as Proto.ErrorResponse
  } catch (e) {
    const errMsg =
      e && typeof e === 'object' && 'message' in e
        ? (e as Error).message
        : String(e)
    jsLogger.error('interactionStart error', e)
    // Return a default Proto.ErrorResponse object
    return {
      error: true,
      message: errMsg || 'Failed to start conversation'
    }
  }
}

export const checkInterrupt = async (interactionId: string, reply: string) => {
  try {
    const url = `${config.apiUrl}/two_factor/checkInterrupt`
    const params = {
      interactionId,
      reply
    }
    const startTime = Date.now()
    const req = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params)
    })
    const res = await req.json()
    jsLogger.log('checkInterrupt latency', Date.now() - startTime, reply)
    jsLogger.log('checkInterrupt res', res)
    const interrupt = get(res, 'interrupt', false)
    return interrupt
  } catch (e) {
    jsLogger.warn('checkInterrupt error', e)
    // Sentry.captureException(e)
    return false
  }
}

export const getRecapInfo = async (interactionId: string) => {
  const url = `${config.apiUrl}/two_factor/${interactionId}/recap`
  const req = await fetch(url, {
    method: 'GET',
    priority: 'low',
    headers
  })
  if (req.ok) {
    const res = (await req.json()) as TwoFactorT.RecapT
    jsLogger.log('getRecapInfo res', res)
    return res
  } else {
    jsLogger.warn('getRecapInfo error', req.statusText)
    return null
  }
}

export const dbGetLocalization = async () => {
  const url = `${config.apiUrl}/demo2/localization`
  const req = await fetch(url, {
    method: 'GET',
    priority: 'low',
    headers
  })
  if (req.ok) {
    const res = (await req.json()) as Record<string, Record<string, string>>
    jsLogger.log('dbGetLocalization res', res)
    return res
  } else {
    jsLogger.warn('dbGetLocalization error', req.statusText)
    return null
  }
}

export const interactionSetLang = async (
  interactionId: string,
  lang: string
) => {
  try {
    jsLogger.log('interactionSetLang', { lang })
    const url = `${config.apiUrl}/two_factor/${interactionId}/setLang`
    const req = await fetch(url, {
      method: 'POST',
      headers,
      priority: 'low',
      body: JSON.stringify({
        lang
      })
    })
    const data = await req.json()
    jsLogger.log('interactionSetLang response', data)
    return null
  } catch (e) {
    jsLogger.error('interactionSetLang error', { error: e })
    return null
  }
}

export const generateId = (): string => {
  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
  const day = currentDate.getDate().toString().padStart(2, '0')
  const hours = currentDate.getHours().toString().padStart(2, '0')
  const minutes = currentDate.getMinutes().toString().padStart(2, '0')
  const seconds = currentDate.getSeconds().toString().padStart(2, '0')
  const milliseconds = currentDate.getMilliseconds().toString().padStart(3, '0')
  const id = `${year}${month}${day}${hours}${minutes}${seconds}${milliseconds}`
  return id
}

// export const sendError = async (
//   event: Sentry.Event,
//   hint: Sentry.EventHint
// ) => {
//   const url = `${config.apiUrl}/demo3/error`
//   const req = await fetch(url, {
//     method: 'POST',
//     priority: 'low',
//     body: JSON.stringify({ event, hint }),
//     headers
//   })
//   const data = await req.json()

//   jsLogger.log('sendError response', data)
//   return data
// }

export const checkIsWebMSupported = () => {
  var video = document.createElement('video')

  return video.canPlayType('video/webm') !== ''
}

export const dbSendLatencies = async (
  interactionId: string,
  latencies: Partial<SessionLatencies>
) => {
  try {
    jsLogger.log('dbSendLatencies', { interactionId, latencies })
    const url = `${config.backendUrl}/demo2/${interactionId}/latencies`
    const req = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers
      },
      priority: 'low',
      body: JSON.stringify({
        latencies
      })
    })
    const data = await req.json()
    jsLogger.log('dbSendLatencies response', data)
    return null
  } catch (e) {
    jsLogger.error('dbSendLatencies error', { error: e })
    return null
  }
}

export const dbSendAvatarLatency = async (
  sessionId: string,
  params: AvatarLatencyRequest
) => {
  try {
    const url = `${config.backendUrl}/demo3/${sessionId}/avatar_latency`
    const req = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params)
    })
    const res = await req.json()
    jsLogger.log('sendAvatarLatency res', res)
    return res
  } catch (e) {
    jsLogger.warn('sendAvatarLatency error', e)
    // Sentry.captureException(e)
    return
  }
}

export const getSessionReport = async (sessionId: string) => {
  try {
    const url = `${config.backendUrl}/demo3/${sessionId}/get-session-report`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    const data = (await response.json()) as
      | Proto.SessionReportResponse
      | Proto.ErrorResponse
    console.log('getSessionReport data', data)
    return data
  } catch (error) {
    console.error('Fetch error:', error)
    return null
  }
}

export const dbRegisterUser = async (
  sessionId: string,
  name: string,
  mediaType: string,
  photo: Blob
): Promise<string | null> => {
  const url =
    `${config.backendUrl}/demo3/${encodeURIComponent(
      sessionId
    )}/register_user` +
    `?name=${encodeURIComponent(name)}&mediaType=${encodeURIComponent(
      mediaType
    )}`

  try {
    const response = await fetchWithRetries(url, {
      method: 'POST',
      headers: {},
      body: photo,
      timeoutMs: 40000
    })
    const data = await response.json()
    jsLogger.log('dbRegisterUser response:', data)
    if (data && typeof data.userId === 'string') {
      return data.userId
    }
    // If the response contains an error or does not have userId, return null
    return null
  } catch (e) {
    console.error('dbRegisterUser fetch error', e)
    return null
  }
}

export const sendSessionStarted = async (sessionId: string) => {
  const url = `${config.apiUrl}/demo3/${sessionId}/session_started`
  const req = await fetchWithRetries(url, {
    method: 'GET',
    priority: 'low',
    headers
  })
  if (req.ok) {
    jsLogger.log('sendSessionStarted sent')
    return null
  } else {
    jsLogger.warn('getRecapInfo error', req.statusText)
    return null
  }
}
